import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Paper,
} from '@mui/material';
import { ShoppingCart, Add, Remove } from '@mui/icons-material';
import { getProducts, createOrder, getShippingCost } from '../../services/api';
import { Product } from '../../types/shop';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import GuestCheckout from './GuestCheckout';

interface ShopProps {
  barberId?: number;
}

interface CartItem {
  product: Product;
  quantity: number;
}

const Shop: React.FC<ShopProps> = ({ barberId: propBarberId }) => {
  const { barberId: paramBarberId } = useParams<{ barberId: string }>();
  const actualBarberId = propBarberId || Number(paramBarberId);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (actualBarberId) {
      fetchProducts();
      fetchShippingCost();
    }
  }, [actualBarberId]);

  const fetchProducts = async () => {
    try {
      const data = await getProducts(actualBarberId);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchShippingCost = async () => {
    try {
      const cost = await getShippingCost(actualBarberId);
      setShippingCost(cost);
    } catch (error) {
      console.error('Error fetching shipping cost:', error);
    }
  };

  const addToCart = (product: Product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product.id === product.id && item.quantity < product.stock
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId: number, delta: number) => {
    setCart(prevCart => {
      return prevCart
        .map(item =>
          item.product.id === productId
            ? { 
                ...item, 
                quantity: Math.max(0, Math.min(item.quantity + delta, item.product.stock))
              }
            : item
        )
        .filter(item => item.quantity > 0);
    });
  };

  const handleCheckout = async () => {
    if (!user) {
      setShowGuestCheckout(true);
      setCartOpen(false);
      return;
    }

    setLoading(true);
    try {
      await createOrder({
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
        }))
      });
      setCart([]);
      setCartOpen(false);
      // You might want to show a success message here
    } catch (error) {
      console.error('Error creating order:', error);
      // You might want to show an error message here
    } finally {
      setLoading(false);
    }
  };

  const handleGuestCheckoutSuccess = () => {
    setCart([]);
    setShowGuestCheckout(false);
  };

  const handleGuestCheckoutCancel = () => {
    setShowGuestCheckout(false);
    setCartOpen(true);
  };

  if (showGuestCheckout) {
    return (
      <GuestCheckout 
        cart={cart} 
        onSuccess={handleGuestCheckoutSuccess} 
        onCancel={handleGuestCheckoutCancel}
        barberId={actualBarberId}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4">Shop</Typography>
        <IconButton 
          onClick={() => setCartOpen(true)}
          sx={{ color: 'primary.main' }}
        >
          <Badge badgeContent={cart.length} color="primary">
            <ShoppingCart />
          </Badge>
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(20px)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {product.image_url && (
                <Box
                  component="img"
                  src={product.image_url}
                  alt={product.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
              )}
              <Box sx={{ p: 2, flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {product.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    mb: 1 
                  }}
                >
                  {product.description}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'primary.main',
                    mb: 1 
                  }}
                >
                  ${product.price}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: product.stock > 0 ? 'text.secondary' : 'error.main'
                  }}
                >
                  {product.stock > 0 ? `In Stock: ${product.stock}` : 'Out of Stock'}
                </Typography>
                <Button 
                  fullWidth 
                  variant="contained"
                  onClick={() => addToCart(product)}
                  disabled={product.stock === 0}
                  sx={{
                    mt: 2,
                    backgroundColor: 'primary.main',
                    color: 'text.light',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'text.disabled',
                    }
                  }}
                >
                  Add to Cart
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            width: 350,
            background: (theme) => theme.palette.background.paper,
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ color: 'text.primary' }}
          >
            Shopping Cart
          </Typography>
          {cart.length === 0 ? (
            <Typography sx={{ color: 'text.secondary' }}>
              Your cart is empty
            </Typography>
          ) : (
            <>
              <List>
                {cart.map((item) => (
                  <React.Fragment key={item.product.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Typography sx={{ color: 'text.primary' }}>
                            {item.product.name}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ color: 'text.secondary' }}>
                            ${item.product.price} x {item.quantity}
                          </Typography>
                        }
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.product.id, -1)}
                          sx={{ color: 'text.primary' }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography 
                          component="span" 
                          sx={{ mx: 1, color: 'text.primary' }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.product.id, 1)}
                          disabled={item.quantity >= item.product.stock}
                          sx={{ color: 'text.primary' }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Subtotal: ${cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Shipping: ${shippingCost.toFixed(2)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ color: 'text.primary' }}
                >
                  Total: ${(cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0) + shippingCost).toFixed(2)}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCheckout}
                  disabled={loading}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'text.light',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  {loading ? 'Processing...' : user ? 'Checkout' : 'Checkout as Guest'}
                </Button>
                
                {!user && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/login')}
                    sx={{ mt: 1 }}
                  >
                    Login to Checkout
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </Container>
  );
};

export default Shop;